/**
 * AddressConfirmationDialog — shown when Google resolves a submitted address
 * to a different one.
 *
 * Nothing has been saved at this point. The operator either accepts Google's
 * version or returns to the form to correct the address themselves. There is
 * deliberately no "keep mine": storing the typed text while keeping Google's
 * coordinates would put the manifest and the delivery location in
 * disagreement. See TCA-26092105.
 */
import React from 'react';
import { validAddressProposal } from '@/utils/addressConfirmation';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const FIELD_LABELS = {
  recipient_street_address: 'Street',
  recipient_city: 'City',
  recipient_state: 'State',
  recipient_zip_code: 'Zip Code',
};

const FIELD_ORDER = [
  'recipient_street_address',
  'recipient_city',
  'recipient_state',
  'recipient_zip_code',
];

export function AddressConfirmationDialog({ difference, proposal, reason = 'review_required', onAccept, onEdit, busy = false }) {
  const rows = FIELD_ORDER.filter((field) => difference?.[field]);
  const canAccept = validAddressProposal(proposal) && reason !== 'saved_address_changed';
  const messages = {
    proposal_changed: 'The address lookup changed since your last review. Nothing has been saved. Review this suggestion before choosing it.',
    lookup_unavailable: 'The reviewed address could not be checked. Nothing has been saved. Your entries are retained; retry the check or return to editing.',
    saved_address_changed: 'The saved address changed while you were reviewing. Nothing from this save was stored. Return to editing and check the current order before trying again.',
    client_update_required: 'This address confirmation requires a compatible app and server. Your entries are retained. Return to editing; update the app before confirming.',
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onEdit?.()}>
      <DialogContent
        data-testid="address-confirmation-dialog"
        className="max-w-lg bg-[#fdfcfa]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          e.currentTarget.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold text-[#1a1a1a]">
            Review the address
          </DialogTitle>
          <DialogDescription className="text-[13px] text-black/60">
            {messages[reason] || "Google matched what you entered to a different address. Nothing has been saved yet."}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-hidden rounded-xl border border-black/[0.07]">
          <div className="grid grid-cols-[auto_1fr_1fr] gap-x-4 border-b border-black/[0.07] bg-black/[0.02] px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-black/40">
            <span />
            <span>You entered</span>
            <span>Google suggests</span>
          </div>
          {rows.map((field) => (
            <div
              key={field}
              className="grid grid-cols-[auto_1fr_1fr] gap-x-4 border-b border-black/[0.05] px-4 py-2 text-[13px] last:border-b-0"
              data-testid={`address-difference-${field}`}
            >
              <span className="text-black/40">{FIELD_LABELS[field]}</span>
              <span className="text-[#1a1a1a]">{difference[field].entered}</span>
              <span className="font-medium text-[#1a1a1a]">
                {difference[field].proposed}
              </span>
            </div>
          ))}
        </div>

        <DialogFooter className="mt-2 gap-2">
          <Button
            variant="outline"
            onClick={onEdit}
            disabled={busy}
            data-testid="address-confirmation-edit"
          >
            Let me fix it
          </Button>
          <Button
            onClick={onAccept}
            disabled={busy || !canAccept}
            data-testid="address-confirmation-accept"
          >
            {reason === 'lookup_unavailable' ? 'Retry address check' : "Use Google's address"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddressConfirmationDialog;
